import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGE_STYLES, fmtDate, Loading } from "@/components/shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Phone, MessageCircle, PhoneCall, Clock, ArrowRightLeft, CalendarPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const OUTCOME_LABEL = {
  no_answer: "Not picked", busy: "Busy", switched_off: "Switched off / unreachable",
  wrong_number: "Wrong number", connected: "Connected", callback_requested: "Callback requested",
  book_demo: "Book demo", book_counselling: "Book counselling",
};

function LeadNotes({ leadId }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const { data } = useQuery({ queryKey: ["lead-notes", leadId], queryFn: async () => (await api.get(`/leads/${leadId}/notes`)).data });
  const add = useMutation({
    mutationFn: async () => (await api.post(`/leads/${leadId}/notes`, { text })).data,
    onSuccess: () => { toast.success("Note added"); setText(""); qc.invalidateQueries({ queryKey: ["lead-notes", leadId] }); qc.invalidateQueries({ queryKey: ["timeline", leadId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="lead-notes">
      <div className="font-semibold">Notes</div>
      {data?.original && <div className="bg-amber-50 dark:bg-amber-950/30 rounded p-2 text-xs" data-testid="lead-original-note"><b>Original note:</b> {data.original}</div>}
      <div className="flex gap-2">
        <Textarea rows={2} placeholder="Add a note for future reference…" value={text} onChange={(e) => setText(e.target.value)} data-testid="lead-note-input" />
        <Button className="self-end bg-brand-blue hover:bg-blue-700" disabled={!text.trim() || add.isPending} onClick={() => add.mutate()} data-testid="lead-note-save">Add</Button>
      </div>
      <div className="space-y-1.5 max-h-60 overflow-y-auto">
        {(data?.notes || []).map((n) => (
          <div key={n.id} className="text-xs border-b pb-1.5" data-testid="lead-note-item">
            <div className="text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{n.note}</div>
            <div className="text-[10px] text-slate-400">{n.type === "note" ? "Note" : n.type.replace(/_/g, " ")}{n.outcome ? ` · ${OUTCOME_LABEL[n.outcome] || n.outcome}` : ""} · {n.actor_name} · {fmtDate(n.created_at)}</div>
          </div>
        ))}
        {!data?.notes?.length && <p className="text-xs text-slate-400">No notes yet</p>}
      </div>
    </div>
  );
}

function AssignmentInfo({ lead, staff }) {
  const { data: hist } = useQuery({ queryKey: ["assign-hist", lead.id, lead.assigned_to], queryFn: async () => (await api.get(`/leads/${lead.id}/assignments`)).data });
  const name = (staff || []).find((s) => s.id === lead.assigned_to)?.name || lead.assigned_name;
  return (
    <div className="rounded-lg border p-3 space-y-1.5" data-testid="assignment-info">
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500">Assigned to</span>
        <span className="font-semibold" data-testid="assigned-name">{name || "Unassigned"}
          {lead.assigned_via === "auto" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">Auto (rotation)</span>}
        </span>
      </div>
      {lead.assigned_at && <div className="text-[11px] text-slate-400" data-testid="assigned-meta">{lead.assigned_via === "auto" ? "Automatically assigned" : `Assigned by ${lead.assigned_by_name || "staff"}`} · {fmtDate(lead.assigned_at)}</div>}
      {!!hist?.length && (
        <div className="pt-1 border-t space-y-1" data-testid="assignment-history">
          {hist.map((h, i) => (
            <div key={i} className="text-[11px] text-slate-500">{fmtDate(h.at)} · {h.from ? `${h.from} → ` : ""}<b>{h.to || "Unassigned"}</b> · {h.via === "auto" ? "Auto (rotation)" : `by ${h.by || "staff"}`}</div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function LeadDrawer({ leadId, config, onClose, onChanged }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");

  const { data: lead, isLoading } = useQuery({ queryKey: ["lead", leadId], queryFn: async () => (await api.get(`/leads/${leadId}`)).data });
  const { data: timeline } = useQuery({ queryKey: ["timeline", leadId], queryFn: async () => (await api.get(`/leads/${leadId}/timeline`)).data });
  const { data: staff } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/users")).data, enabled: ["admin", "reception"].includes(user?.role) });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["lead", leadId] }); qc.invalidateQueries({ queryKey: ["timeline", leadId] }); onChanged?.(); };

  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [followAt, setFollowAt] = useState("");
  const [bookOpen, setBookOpen] = useState(false);
  const [appt, setAppt] = useState({ kind: "demo", scheduled_at: "", note: "", counsellor_id: "" });
  const [delOpen, setDelOpen] = useState(false);
  const removeLead = useMutation({
    mutationFn: async () => (await api.delete(`/leads/${leadId}`)).data,
    onSuccess: () => { toast.success("Lead deleted"); setDelOpen(false); onChanged?.(); onClose?.(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const bookVisit = useMutation({
    mutationFn: async () => (await api.post("/appointments", {
      lead_id: leadId, lead_name: lead.name, phone: lead.phone, kind: appt.kind,
      scheduled_at: new Date(appt.scheduled_at).toISOString(), note: appt.note,
      counsellor_id: appt.counsellor_id || lead.assigned_to || (user?.role === "sales" ? user._id : null),
    })).data,
    onSuccess: () => {
      toast.success("Counselling/demo booked — lead moved to Visit/Demo Booked");
      setBookOpen(false);
      setAppt({ kind: "demo", scheduled_at: "", note: "", counsellor_id: "" });
      invalidate();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const logActivity = useMutation({
    mutationFn: async (payload) => (await api.post(`/leads/${leadId}/activity`, payload)).data,
    onSuccess: (data, variables) => {
      toast.success("Logged");
      setOutcome(""); setNote(""); setFollowAt("");
      invalidate();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const changeStage = useMutation({
    mutationFn: async (payload) => (await api.post(`/leads/${leadId}/stage`, payload)).data,
    onSuccess: () => { toast.success("Stage updated"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const assign = useMutation({
    mutationFn: async (assigned_to) => (await api.post(`/leads/${leadId}/assign`, { assigned_to })).data,
    onSuccess: () => { toast.success("Reassigned"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [lostReason, setLostReason] = useState("");

  const digits = (lead?.phone || "").replace(/\D/g, "");
  const waMsg = (settings?.message_templates?.initial || "Namaste {name}")
    .replace("{name}", lead?.name || "").replace("{course}", lead?.course_interest || "our courses");
  const waLink = `https://wa.me/91${digits.slice(-10)}?text=${encodeURIComponent(waMsg)}`;

  const openLink = (type, url) => {
    api.post(`/leads/${leadId}/activity`, { type: "link_open", note: `${type} link opened` }).then(invalidate);
    window.open(url, "_blank");
  };

  return (
    <>
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="lead-drawer">
        {isLoading || !lead ? <Loading /> : (
          <div>
            <SheetHeader className="p-5 border-b sticky top-0 bg-white dark:bg-slate-900 z-10">
              <SheetTitle className="flex items-center justify-between">
                <span className="font-head text-xl">{lead.name}</span>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${STAGE_STYLES[lead.stage]}`}>{lead.stage}</span>
              </SheetTitle>
              <SheetDescription className="sr-only">Lead details, actions and activity timeline</SheetDescription>
              {user?.role !== "marketing" && <div className="flex gap-2 pt-2 flex-wrap">
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openLink("Call", `tel:${digits}`)} data-testid="click-to-call">
                  <PhoneCall className="w-4 h-4 text-brand-blue" /> Call
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openLink("WhatsApp", waLink)} data-testid="click-to-whatsapp">
                  <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp
                </Button>
              </div>}
              {user?.role !== "marketing" && <p className="text-[11px] text-slate-400 pt-1">Opening a link is logged as a link-open only — not a completed call or delivered message.</p>}
            </SheetHeader>

            <Tabs value={tab} onValueChange={setTab} className="p-5">
              <TabsList className={`grid w-full ${user?.role === "marketing" ? "grid-cols-2" : "grid-cols-3"}`}>
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                {user?.role !== "marketing" && <TabsTrigger value="action" data-testid="tab-action">Log Action</TabsTrigger>}
                <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-3 mt-4 text-sm">
                {[["Phone", lead.phone], ["Alt phone", lead.alt_phone], ["Email", lead.email], ["Locality", lead.locality],
                  ["Occupation", lead.occupation], ["Course interest", lead.course_interest], ["Preferred timing", lead.preferred_timings],
                  ["Source", `${lead.first_source} (latest: ${lead.latest_source})`], ["Priority", lead.priority],
                  ["Next follow-up", lead.next_follow_up_at ? fmtDate(lead.next_follow_up_at) : "—"],
                  ["Last call result", OUTCOME_LABEL[lead.last_call_outcome] || "—"],
                  ["Created by", `${lead.created_by_name || "—"}${lead.created_by_role ? ` (${lead.created_by_role})` : ""}`],
                  ["Created on", lead.created_at ? fmtDate(lead.created_at) : "—"],
                  ["Last contact", lead.last_contact ? fmtDate(lead.last_contact) : "—"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-dashed py-1.5">
                    <span className="text-slate-500">{k}</span><span className="font-medium text-right">{v || "—"}</span>
                  </div>
                ))}
                <LeadNotes leadId={leadId} />

                {user?.role !== "marketing" && <div className="pt-2">
                  <Label>Change stage</Label>
                  <div className="flex gap-2 mt-1">
                    <Select onValueChange={(v) => v === "Lost" ? setTab("action") : changeStage.mutate({ stage: v })}>
                      <SelectTrigger data-testid="stage-select"><SelectValue placeholder={lead.stage} /></SelectTrigger>
                      <SelectContent>{(config?.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>}

                {["admin", "reception", "marketing"].includes(user?.role) && <AssignmentInfo lead={lead} staff={staff} />}
                {user?.role === "marketing" && (
                  <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950/30 p-3 text-xs text-indigo-800 dark:text-indigo-200" data-testid="marketing-status-note">
                    Current status: <b>{lead.stage}</b>{lead.stage === "Enrolled" ? " — converted to admission" : lead.stage === "Lost" ? ` — ${lead.lost_reason || "not converted"}` : ""}. Sales/reception handle this lead; ask {lead.assigned_name || "the assignee"} for an update.
                  </div>
                )}

                {["admin", "reception"].includes(user?.role) && (
                  <div className="pt-2">
                    <Label className="flex items-center gap-1"><ArrowRightLeft className="w-3.5 h-3.5" /> Transfer / assign to</Label>
                    <Select value={lead.assigned_to || "none"} onValueChange={(v) => assign.mutate(v === "none" ? null : v)}>
                      <SelectTrigger className="mt-1" data-testid="assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        {user?.role === "admin" && <SelectItem value="none">Unassigned</SelectItem>}
                        {(staff || []).filter((s) => s.active !== false && s.role !== "marketing" && (user?.role === "admin" || s.role === "sales")).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {user?.role === "reception" && <p className="text-[11px] text-slate-400 mt-1">Reception can assign leads to sales counsellors.</p>}
                  </div>
                )}

                {["Lost", "Invalid", "Nurture"].includes(lead.stage) && user?.role !== "marketing" && (
                  <Button variant="outline" className="w-full mt-2" onClick={() => api.post(`/leads/${leadId}/reopen`).then(() => { toast.success("Reopened"); invalidate(); })} data-testid="reopen-btn">
                    Reopen (preserve history)
                  </Button>
                )}
                {user?.role === "admin" && (
                  <Button variant="outline" className="w-full mt-2 text-red-600 border-red-200 hover:bg-red-50 gap-1.5" onClick={() => setDelOpen(true)} data-testid="lead-delete-btn">
                    <Trash2 className="w-4 h-4" /> Delete lead
                  </Button>
                )}
              </TabsContent>

              <TabsContent value="action" className="space-y-4 mt-4">
                <div>
                  <Label>Call outcome</Label>
                  <Select value={outcome} onValueChange={setOutcome}>
                    <SelectTrigger data-testid="outcome-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                    <SelectContent>{(config?.call_outcomes || []).map((o) => <SelectItem key={o} value={o}>{OUTCOME_LABEL[o]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened?" data-testid="action-note" /></div>
                <div>
                  <Label>Next follow-up {outcome === "callback_requested" && <span className="text-red-500">(required for callback)</span>}</Label>
                  <Input type="datetime-local" value={followAt} onChange={(e) => setFollowAt(e.target.value)} data-testid="followup-datetime" />
                </div>
                <Button className="w-full bg-brand-blue hover:bg-blue-700" data-testid="log-call-btn"
                  onClick={() => {
                    if (outcome === "book_demo" || outcome === "book_counselling") { setAppt((a) => ({ ...a, kind: outcome === "book_demo" ? "demo" : "counselling" })); setBookOpen(true); return; }
                    logActivity.mutate({ type: "call", outcome: outcome || undefined, note, next_follow_up_at: followAt ? new Date(followAt).toISOString() : undefined });
                  }}>
                  {outcome === "book_demo" || outcome === "book_counselling" ? "Continue to booking" : "Save call outcome"}
                </Button>
                {(outcome === "book_demo" || outcome === "book_counselling") && (
                  <p className="text-xs text-orange-600 flex items-center gap-1"><CalendarPlus className="w-3.5 h-3.5" /> The counselling/demo booking form will open next.</p>
                )}

                <div className="border-t pt-4">
                  <Label>Mark as Lost</Label>
                  <Select value={lostReason} onValueChange={setLostReason}>
                    <SelectTrigger className="mt-1" data-testid="lost-reason-select"><SelectValue placeholder="Lost reason" /></SelectTrigger>
                    <SelectContent>{(config?.lost_reasons || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="destructive" className="w-full mt-2" disabled={!lostReason} data-testid="mark-lost-btn"
                    onClick={() => changeStage.mutate({ stage: "Lost", lost_reason: lostReason, note })}>Mark Lost</Button>
                </div>
              </TabsContent>

              <TabsContent value="timeline" className="mt-4 space-y-3">
                {!timeline ? <Loading /> : (
                  <>
                    {[...(timeline.activities || [])].map((a) => (
                      <div key={a.id} className="flex gap-3 text-sm" data-testid="timeline-item">
                        <div className="w-2 h-2 rounded-full bg-brand-blue mt-1.5 shrink-0" />
                        <div className="flex-1">
                          <div className="font-medium">{a.type.replace(/_/g, " ")}{a.outcome ? ` — ${OUTCOME_LABEL[a.outcome] || a.outcome}` : ""}</div>
                          {a.note && <div className="text-slate-500">{a.note}</div>}
                          <div className="text-[11px] text-slate-400">{fmtDate(a.created_at)} • {a.actor_name}</div>
                        </div>
                      </div>
                    ))}
                    {(timeline.enquiry_events || []).length > 1 && (
                      <div className="text-xs text-slate-500 border-t pt-2">Enquiry events: {timeline.enquiry_events.length} (repeat enquiries preserved)</div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>

    <Dialog open={bookOpen} onOpenChange={setBookOpen}>
      <DialogContent className="max-w-md" data-testid="book-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-orange-600" /> Book Visit / Demo — {lead?.name}</DialogTitle>
          <DialogDescription>Book a counselling or demo session for this lead. Lead stage moves to Visit/Demo Booked.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Session type</Label>
            <Select value={appt.kind} onValueChange={(v) => setAppt((a) => ({ ...a, kind: v }))}>
              <SelectTrigger data-testid="appt-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="counselling">Counselling</SelectItem><SelectItem value="demo">Demo</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date &amp; time *</Label>
            <Input type="datetime-local" value={appt.scheduled_at} onChange={(e) => setAppt((a) => ({ ...a, scheduled_at: e.target.value }))} data-testid="appt-datetime" />
          </div>
          {staff && (
            <div>
              <Label>Counsellor</Label>
              <Select value={appt.counsellor_id || "auto"} onValueChange={(v) => setAppt((a) => ({ ...a, counsellor_id: v === "auto" ? "" : v }))}>
                <SelectTrigger data-testid="appt-counsellor"><SelectValue placeholder="Assigned counsellor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Assigned counsellor</SelectItem>
                  {(staff || []).filter((s) => s.role === "sales" && s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Note</Label>
            <Textarea value={appt.note} onChange={(e) => setAppt((a) => ({ ...a, note: e.target.value }))} placeholder="e.g. Wants to see a live Meta Ads demo" data-testid="appt-note" />
          </div>
        </div>
        <DialogFooter>
          <Button className="bg-orange-600 hover:bg-orange-700 w-full gap-2" disabled={!appt.scheduled_at || bookVisit.isPending} onClick={() => bookVisit.mutate()} data-testid="appt-book-confirm">
            <CalendarPlus className="w-4 h-4" /> Confirm Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={delOpen} onOpenChange={setDelOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Delete lead?</DialogTitle>
          <DialogDescription>This permanently removes {lead?.name} and their entire enquiry history, activities, follow-ups and appointments. This cannot be undone. A lead with an existing admission cannot be deleted.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setDelOpen(false)} data-testid="lead-delete-cancel">Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700" disabled={removeLead.isPending} onClick={() => removeLead.mutate()} data-testid="lead-delete-confirm">Delete permanently</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
